import React, { useMemo } from 'react'
import { Box, List, ListItemButton, Stack, Typography } from '@mui/material'
import type { AiConversationSummary } from '../../../services/apis/aiChat'

type Props = {
    conversations: AiConversationSummary[]
    activeConversationId: string | null
    onSelect: (conversationId: string) => void
}

function startOfDay(date: Date): Date {
    const copy = new Date(date)
    copy.setHours(0, 0, 0, 0)
    return copy
}

function formatHistoryDate(updatedAt?: string): string {
    if (!updatedAt) return 'Earlier'
    const parsed = new Date(updatedAt)
    if (Number.isNaN(parsed.getTime())) return 'Earlier'

    const today = startOfDay(new Date())
    const day = startOfDay(parsed)
    const diffDays = Math.round((today.getTime() - day.getTime()) / 86_400_000)

    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Yesterday'
    return `${parsed.getDate()}/${parsed.getMonth() + 1}/${parsed.getFullYear()}`
}

function formatHistoryTime(updatedAt?: string): string {
    if (!updatedAt) return ''
    const parsed = new Date(updatedAt)
    if (Number.isNaN(parsed.getTime())) return ''
    return parsed.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
}

function groupConversationsByDate(conversations: AiConversationSummary[]) {
    const groups: { dateLabel: string; items: AiConversationSummary[] }[] = []
    let currentLabel = ''

    for (const conversation of conversations) {
        const dateLabel = formatHistoryDate(conversation.updatedAt)
        if (dateLabel !== currentLabel) {
            groups.push({ dateLabel, items: [conversation] })
            currentLabel = dateLabel
        } else {
            groups[groups.length - 1].items.push(conversation)
        }
    }

    return groups
}

export default function ChatHistoryPanel({ conversations, activeConversationId, onSelect }: Props) {
    const groups = useMemo(() => groupConversationsByDate(conversations), [conversations])

    if (conversations.length === 0) {
        return (
            <Typography
                variant="caption"
                color="text.secondary"
                align="left"
                sx={{ display: 'block', px: 2, py: 1.5 }}
            >
                No conversations yet
            </Typography>
        )
    }

    return (
        <Box sx={{ width: '100%', textAlign: 'left' }}>
            {groups.map((group, groupIndex) => (
                <Box key={group.dateLabel} sx={{ width: '100%' }}>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        align="left"
                        sx={{
                            display: 'block',
                            px: 2,
                            pt: groupIndex === 0 ? 1 : 1.5,
                            pb: 0.5,
                            fontSize: '0.6875rem',
                            fontWeight: 500,
                        }}
                    >
                        {group.dateLabel}
                    </Typography>
                    <List disablePadding sx={{ width: '100%' }}>
                        {group.items.map((conversation) => {
                            const title = conversation.title?.trim() || 'New chat'
                            const selected = conversation.id === activeConversationId
                            const timeLabel = formatHistoryTime(conversation.updatedAt)

                            return (
                                <ListItemButton
                                    key={conversation.id}
                                    selected={selected}
                                    onClick={() => onSelect(conversation.id)}
                                    alignItems="center"
                                    sx={{
                                        width: '100%',
                                        px: 2,
                                        py: 0.875,
                                        borderRadius: 0,
                                        textAlign: 'left',
                                        justifyContent: 'flex-start',
                                        borderBottom: 1,
                                        borderColor: 'divider',
                                        '&.Mui-selected': {
                                            bgcolor: 'action.selected',
                                        },
                                        '&.Mui-selected:hover': {
                                            bgcolor: 'action.selected',
                                        },
                                    }}
                                >
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        spacing={1.5}
                                        sx={{ width: '100%', minWidth: 0 }}
                                    >
                                        <Typography
                                            variant="body2"
                                            noWrap
                                            align="left"
                                            sx={{
                                                flex: 1,
                                                minWidth: 0,
                                                fontSize: '0.8125rem',
                                                fontWeight: selected ? 500 : 400,
                                                lineHeight: 1.35,
                                                textAlign: 'left',
                                            }}
                                        >
                                            {title}
                                        </Typography>
                                        {timeLabel && (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ flexShrink: 0, fontSize: '0.6875rem' }}
                                            >
                                                {timeLabel}
                                            </Typography>
                                        )}
                                    </Stack>
                                </ListItemButton>
                            )
                        })}
                    </List>
                </Box>
            ))}
        </Box>
    )
}
